// Restored verbatim from src/pages/RFQ/RFQ/Views/GridView/RFQCard/RFQCard.js
// @ 310ebe2d4^:src/pages/RFQ/RFQ/Views/GridView/RFQCard/RFQCard.js (TASK-027)
//
// Adapter-layer changes from the source (see TASK_SUMMARY-027.md for the
// full line-by-line diff report):
//  - `@reach/router` `Link`/`navigate` swapped for `RfxRouterLink`/
//    `useRfxNavigate` (`newui/` bans direct `@reach/router` imports).
//  - Local `EditDropdown` action menu + its own `ExtendSubmissionDeadlineModal`/
//    redux dispatch replaced with TASK-018's `RfxRowActions` (status-driven,
//    not tab-key-driven) in the SAME corner slot, and `onExtendClick` lifted
//    to a prop so the parent list owns one shared extend-deadline modal.
//    ESCALATION: RFQCard's original action affordance (`EditDropdown`, a
//    dropdown menu) visually differs from `RfxRowActions` (inline icon
//    buttons) — see TASK_SUMMARY-027.md / final report for the flagged
//    placement conflict (brief item 7); this is the least-invasive wiring,
//    not a self-authorized design decision.
//  - Added `data-testid={`rfx-row-${rfq?.id}`}` on the card root (TASK-017/018
//    testid convention) — attribute-only.
//  - TASK-027 (this task, second pass): team-lead ruled this SAME component
//    is shared verbatim by Panel/Approvals too (`PanelList`/`ApprovalsList`),
//    fed a leaner adapter-mapped row instead of a real `Rfx` (widening
//    `RfxListItemResource` would leak procurer-side data cross-company — the
//    leak class TASK-021 closed). Added minimal conditionals, each guarding
//    a block that reads an owner/T&Q-only field absent from that leaner row:
//     - `rfq?.linkOverride` (FE-only, adapter-set): short-circuits the
//       owner edit/view routing so Panel/Approvals keep their own
//       `getPanelRowTarget`/`getApprovalRowTarget` click target.
//     - `rfq?.supplierInvited` gates: the action-menu slot (owner-only
//       affordance), and the Invited/Accepted/Submitted stat blocks (would
//       otherwise render a misleading literal "0/0" from destructuring
//       defaults instead of omitting the stat).
//    See TASK_SUMMARY-027.md for the full accounting.
import { useMemo } from "react";
import tw, { styled, css } from "twin.macro";
import StatusBadge from "components/RFQ/StatusBadge/StatusBadge";
import { Button } from "newui/components/Button";
import dayjs from "newui/utils/dayjs";
import GlobalAvatarGroup from "components/RFQ/AvatarGroup/AvatarGroup";
import { RFX_STATUS, RFX_TABS } from "utils/constants";
import {
	getCurrentURL,
	timezoneChecker,
	isRFQDraft,
} from "utils/helpers";
import { ProfileAvatar } from "newui/components/ProfileAvatar/ProfileAvatar";
import { getSlug } from "helpers/linkHelper";
import { isRfxStatusMatch } from "newui/features/Rfx/utils";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import UnreadMessage from "newui/components/UnreadMessage";
import { RfxRouterLink } from "app/RfxRouterLink";
import { useRfxNavigate } from "app/useRfxNavigate";
import { RfxRowActions } from "../TendersQuotes/RfxRowActions";
import { getRfxStatusBorderClass } from "../../utils/rfx-status-colour";

export const RFQCardStyled = styled.div(() => [
	// Border COLOUR is deliberately not set here — it comes from the status via
	// `getRfxStatusBorderClass` on the element below, so every card's top edge
	// reads as the same colour as its own status badge.
	tw`w-full min-w-[320px] h-full rounded-md border-t-8 relative flex flex-col`,
	css`
		#card-content {
			padding: 24px;
			background-color: white;
			height: 100%;
			max-height: 480px;
			position: relative;
			display: flex;
			flex-direction: column;
			margin-top: auto;
			align-content: space-between;
			${tw`rounded-b-md`}
			h2 {
				font-weight: 600;
				font-size: 20px;
				color: black;
				margin-top: 12px;
			}
		}

		.card-description {
			display: flex;
			justify-content: space-between;
			font-size: 14px;
			padding-top: 8px;
			padding-bottom: 8px;

			.label-font {
				${tw`font-normal text-gray-text-400 -tracking-[0.015em]`}
			}
		}

		.border-top {
			border-top: 1px solid #eaecf0;
		}
		.bold-font {
			color: #101828;
			font-weight: 600;
		}
		.card-desc-container {
			margin-top: auto;
		}
	`,
]);

const RFQCard = ({ rfq, onExtendClick }) => {
	const slug = useMemo(() => getSlug(rfq?.name ?? "Draft RFx"), [rfq?.name]);
	const {
		accepted = [],
		all = [],
		submitted = [],
	} = rfq?.supplierInvited || {};
	// this is for when draft rfx and user hasn't saved any participants yet (not from BE)
	// then we show attached suppliers with status "Prequalified"
	const isDraft = isRFQDraft(rfq?.status);
	const isSupplierList = rfq?.supplierList?.suppliers;
	const isDraftSupplierNotSavedYet = isDraft && all.length === 0;
	const { clientPaths } = useAuthContext();
	const navigate = useRfxNavigate();

	const totalAll =
		(isDraftSupplierNotSavedYet ? isSupplierList?.length : all.length) || 0;
	const totalAccepted = accepted?.length || 0;
	const totalSubmitted = submitted?.length || 0;

	const renderButtonRFQView = (status) => {
		const statusName =
			typeof status === "string" ? status.toUpperCase() : status;
		return (
			<Button
				btntype="secondary"
				width={"full"}
			>
				{statusName === RFX_STATUS.DRAFT
					? "Edit Draft"
					: statusName === RFX_STATUS.EVALUATION
						? "View Response"
						: "View RFx"}
			</Button>
		);
	};

	// TASK-027 Panel/Approvals adapter: `rfq.linkOverride` is an FE-only field
	// (never from the server) set by PanelList/ApprovalsList's adapters to the
	// scope-correct target (`getPanelRowTarget`/`getApprovalRowTarget`) — those
	// scopes don't use the owner edit/view routing below (no draft-editable
	// concept, and Panel's target depends on `rfx_evaluation_id`, not status).
	// T&Q never sets it, so `clientPaths.editRfx`/`viewRfx` below is unchanged
	// for T&Q rows.
	const linkUrl = rfq?.linkOverride ?? (isRfxStatusMatch(rfq?.status, RFX_STATUS.DRAFT) ? clientPaths?.editRfx(rfq?.id) : clientPaths?.viewRfx(rfq));

	const handleKeyDown = (event) => {
		if (event.key === "Enter") {
			let fullURL = getCurrentURL();
			navigate(`${fullURL}${linkUrl}`);
		}
	};

	const navigateToComms = () => {
		navigate(clientPaths?.viewRfx(rfq, RFX_TABS.COMMS));
	}

	return (
		<RFQCardStyled
			className={`card-shadow focusable rfq-card-${rfq?.id} ${getRfxStatusBorderClass(rfq?.status)}`}
			onKeyDown={handleKeyDown}
			data-testid={`rfx-row-${rfq?.id}`}
		>
			<div id="header-card">
				{/* TASK-027 Panel/Approvals adapter: `RfxRowActions` (archive/
				extend/save-as-template) is an OWNER-only affordance. Panel
				members/approvers are never the RFx owner, and
				`RfxListItemResource` never carries `supplierInvited` (an
				owner-view-only field), so gating on it doubles as the
				owner-scope check — closes the same permission-surface class as
				the missing-data gates below, not just a visual nicety. */}
				{rfq?.supplierInvited && (
					<div
						className="absolute z-50 flex"
						style={{ right: "0px", gap: "8px", padding: "12px", top: "0px" }}
					>
						<RfxRowActions
							rfx={rfq}
							onExtendClick={onExtendClick}
							viewHref={linkUrl}
						/>
					</div>
				)}
			</div>
			<div id="card-content">
				<div className="grid grid-cols-[1fr_20px] gap-2">
					<ProfileAvatar
						imagePath={rfq?.project?.logoPath}
						className="size-[50px] p-1 shadow-md"
					/>
					{rfq?.unreadMessages > 0 && <div className="relative h-full w-full flex justify-end right-10 top-2">
						<UnreadMessage value={rfq?.unreadMessages} onClick={navigateToComms} />
					</div>}
				</div>

				<div className="grid border-b pb-2">
					<h2 className="line-clamp-2 text-xl font-semibold">
						{rfq?.name || "-"}
					</h2>
					<div className="flex flex-row justify-between">
						<h3 className="text-sm text-gray-text-400">
							{rfq?.rfxPublished
								? `Issued on ${dayjs(rfq?.rfxPublished)
									.tz(timezoneChecker(rfq?.rfxPublished || "AWST"))
									.format("DD MMM YYYY")} `
								: rfq?.supplierList?.name || "-"}
						</h3>
						<div className="text-sm text-gray-400">
							{rfq?.rfxType?.name}
						</div>
					</div>
				</div>
				<div className="card-desc-container mt-3">
					<div className="card-description">
						<div className="flex w-full items-center">
							<p className="label-font">RFx status</p>
							<div className="ml-auto">
								<StatusBadge status={rfq?.status} />
							</div>
						</div>
					</div>
					<div className="card-description border-top">
						<p className="label-font">Submission Deadline</p>
						<p className="bold-font pl-2 text-right">
							{rfq?.submissionDeadline
								? dayjs(rfq?.submissionDeadline)
									.tz(timezoneChecker(rfq?.submissionTimezone || "AWST"))
									.format("dddd, D MMMM YYYY")
								: "-"}
						</p>
					</div>
					{/* TASK-027 Panel/Approvals adapter: these three blocks read
					`rfq.supplierInvited` — an owner-view-only field
					`RfxListItemResource` never carries. Without this gate the
					destructuring defaults (`all = []` etc.) would silently render a
					misleading literal "0/0" for Panel/Approvals rows instead of
					omitting the stat, so gate on presence rather than rely on the
					defaults. */}
					{rfq?.supplierInvited && (
						<div className="card-description border-top">
							<div className="flex h-10 w-full items-center">
								<p className="label-font">
									{isDraft ? "Prequalified" : "Invited"}
								</p>
								<div className="ml-auto">
									<div className="flex items-center">
										<p className="text-sm font-normal -tracking-[0.015em] text-gray-400">
											{isDraftSupplierNotSavedYet
												? isSupplierList?.length
												: rfq?.supplierInvited?.all?.length}{" "}
											suppliers
										</p>
										{(isDraftSupplierNotSavedYet && isSupplierList?.length > 0) ||
											rfq?.supplierInvited?.all?.length > 0 ? (
											<GlobalAvatarGroup
												avatarSize="sm"
												classNameContainer="hellow [&_.avatar-image-wrapper>div]:w-8 [&_.avatar-image-wrapper>div]:h-8 [&_.avatar-image-wrapper>div]:min-w-8 [&_.avatar-image-wrapper>div]:min-h-8"
												avatars={
													isDraftSupplierNotSavedYet
														? isSupplierList?.map((company) => ({
															avatar: company?.logo_path,
														}))
														: rfq?.supplierInvited?.all?.map((company) => ({
															avatar: company?.company?.logo_path,
														}))
												}
											/>
										) : null}
									</div>
								</div>
							</div>
						</div>
					)}
					{rfq?.supplierInvited && (
						<div className="card-description border-top">
							<p className="label-font">Accepted</p>
							<p className="bold-font">
								{totalAccepted}/{totalAll}
							</p>
						</div>
					)}
					{rfq?.supplierInvited && (
						<div className="card-description border-top">
							<p className="label-font">Submitted</p>
							<p className="bold-font">
								{totalSubmitted}/{totalAccepted}
							</p>
						</div>
					)}
					<div className="mt-2">
						{<RfxRouterLink to={linkUrl ?? "#"}>{renderButtonRFQView(rfq?.status)}</RfxRouterLink>}
					</div>
				</div>
			</div>
		</RFQCardStyled>
	);
};

export default RFQCard;
